"use client";

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { ChevronRightIcon } from "lucide-react";
import {
  Reasoning,
  ReasoningContent,
  ReasoningTrigger,
  useReasoning,
} from "@/components/ai-elements/reasoning";
import { redactInternalResourceReferencesForDisplay } from "@/lib/ai/id-alias/display-redaction";

function ActivityReasoningDisclosureTrigger({ label }: { label: string }) {
  const { isOpen } = useReasoning();

  return (
    <ReasoningTrigger
      className="w-fit max-w-full gap-1.5 text-[13px] leading-5"
      data-preserve-chat-scroll-on-expand
    >
      <span>{label}</span>
      <ChevronRightIcon
        className={`size-3.5 shrink-0 transition-transform ${
          isOpen ? "rotate-90" : "rotate-0"
        }`}
      />
    </ReasoningTrigger>
  );
}

/**
 * provider reasoning を、現在状態と任意開示の履歴に適した密度で表示する。
 *
 * reasoning 内に固定高のスクロール領域を作らず、会話の StickToBottom に
 * 高さの変化と追従を委ねる。
 */
export function ActivityReasoning({
  isStreaming,
  presentation,
  text,
}: {
  isStreaming: boolean;
  presentation: "live" | "history";
  text: string;
}) {
  const { i18n } = useLingui();
  const label =
    presentation === "live"
      ? i18n._(msg`Analyzing...`)
      : i18n._(msg`Analysis details`);

  return (
    <Reasoning
      className="mb-0 min-w-0"
      data-activity-reasoning={presentation}
      defaultOpen={presentation === "history" ? false : undefined}
      isStreaming={presentation === "live" && isStreaming}
    >
      <ActivityReasoningDisclosureTrigger label={label} />
      <ReasoningContent className="mt-1.5 min-w-0 break-words text-[13px] leading-[1.65] text-foreground/75 [&_p]:!my-0 [&_ol]:!my-1 [&_pre]:!max-h-none [&_ul]:!my-1">
        {redactInternalResourceReferencesForDisplay(text)}
      </ReasoningContent>
    </Reasoning>
  );
}
